import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Clock, UserX, AlertCircle, FileText, Users, Lock, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-medium">Volver al inicio</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Términos y Condiciones
                </h1>
                <p className="text-sm text-gray-500">Luxify</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header del documento */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-12 text-white">
              <div className="flex items-center space-x-4 mb-4">
                <FileText className="h-8 w-8" />
                <h1 className="text-4xl font-bold">
                  Términos y Condiciones de Servicio
                </h1>
              </div>
            </div>

            {/* Contenido principal */}
            <div className="px-8 py-12">
              {/* Introducción */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12 border border-blue-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <Globe className="h-6 w-6 text-blue-600 mr-3" />
                  Bienvenido a Luxify
                </h2>
                <div className="space-y-4">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Estos términos y condiciones rigen el uso de nuestra plataforma de prueba virtual de ropa 
                    con inteligencia artificial. Al acceder y utilizar nuestro servicio, aceptas cumplir con 
                    estos términos en su totalidad.
                  </p>
                  <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      <strong>Importante:</strong> Nuestro servicio utiliza proveedores externos de inteligencia 
                      artificial para generar las imágenes de prueba virtual. Los resultados pueden variar 
                      y no están completamente bajo nuestro control directo.
                    </p>
                  </div>
                </div>
              </div>

              {/* Secciones */}
              <div className="space-y-12">
                {/* 1. Definiciones y Alcance */}
                <section className="border-l-4 border-blue-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <FileText className="h-6 w-6 text-blue-600 mr-3" />
                    1. Definiciones y Alcance del Servicio
                  </h2>
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">¿Qué es Luxify?</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Luxify es una plataforma tecnológica que utiliza inteligencia artificial 
                        para permitir a los usuarios probarse ropa virtualmente mediante la superposición 
                        de imágenes de prendas sobre fotos personales.
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Alcance del Servicio</h3>
                      <ul className="list-disc pl-6 text-gray-700 space-y-2">
                        <li>Creación y gestión de catálogos de productos</li>
                        <li>Prueba virtual de prendas mediante IA</li>
                        <li>Compartir catálogos con clientes</li>
                        <li>Herramientas de análisis básicas</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 2. Uso Aceptable */}
                <section className="border-l-4 border-green-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Users className="h-6 w-6 text-green-600 mr-3" />
                    2. Uso Aceptable del Servicio
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-green-800 mb-3">✅ Uso Permitido</h3>
                      <ul className="text-green-700 space-y-2">
                        <li>• Comercio electrónico legítimo</li>
                        <li>• Marketing y promoción de productos</li>
                        <li>• Experimentación personal</li>
                        <li>• Uso educativo y de investigación</li>
                      </ul>
                    </div>
                    <div className="bg-red-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-red-800 mb-3">❌ Uso Prohibido</h3>
                      <ul className="text-red-700 space-y-2">
                        <li>• Contenido inapropiado o ilegal</li>
                        <li>• Violación de derechos de autor</li>
                        <li>• Actividades fraudulentas</li>
                        <li>• Interferencia con el servicio</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 3. Privacidad y Datos */}
                <section className="border-l-4 border-purple-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Lock className="h-6 w-6 text-purple-600 mr-3" />
                    3. Privacidad y Protección de Datos
                  </h2>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Compromiso con tu Privacidad</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Datos que Recopilamos</h4>
                        <ul className="text-gray-700 space-y-1 text-sm">
                          <li>• Información de cuenta (email, nombre)</li>
                          <li>• Imágenes subidas por el usuario</li>
                          <li>• Datos de uso del servicio</li>
                          <li>• Información de pago (procesada por terceros)</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Cómo Protegemos tus Datos</h4>
                        <ul className="text-gray-700 space-y-1 text-sm">
                          <li>• Encriptación de extremo a extremo</li>
                          <li>• Almacenamiento seguro en la nube</li>
                          <li>• No compartimos datos con terceros</li>
                          <li>• Cumplimiento con normativas de privacidad</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 4. Disponibilidad y Limitaciones */}
                <section className="border-l-4 border-orange-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Clock className="h-6 w-6 text-orange-600 mr-3" />
                    4. Disponibilidad y Limitaciones del Servicio
                  </h2>
                  <div className="space-y-6">
                    <div className="bg-orange-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-orange-800 mb-3">Disponibilidad</h3>
                      <p className="text-orange-700 leading-relaxed">
                        Nos esforzamos por mantener el servicio disponible las 24 horas del día, 
                        pero no podemos garantizar una disponibilidad del 100%. El servicio puede 
                        experimentar interrupciones por mantenimiento, actualizaciones o problemas técnicos.
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                      <h3 className="text-lg font-semibold text-amber-800 mb-4 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        Limitaciones Técnicas
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-white/70 rounded-lg p-4 border border-amber-100">
                          <h4 className="font-semibold text-amber-900 mb-2">Calidad de Imágenes</h4>
                          <p className="text-amber-800 text-sm leading-relaxed">
                            La calidad de la prueba virtual depende directamente de la calidad de las imágenes subidas. 
                            Imágenes borrosas, con poca iluminación o mal encuadradas pueden afectar el resultado final.
                          </p>
                        </div>
                        
                        <div className="bg-white/70 rounded-lg p-4 border border-amber-100">
                          <h4 className="font-semibold text-amber-900 mb-2">Discrepancias en Generación de Imágenes</h4>
                          <p className="text-amber-800 text-sm leading-relaxed mb-3">
                            <strong>Importante:</strong> Pueden existir discrepancias o errores al generar las imágenes que 
                            <span className="font-semibold text-amber-900"> no son problema de Luxify</span>, sino de los 
                            proveedores externos del servicio de inteligencia artificial.
                          </p>
                          <p className="text-amber-800 text-sm leading-relaxed">
                            Nosotros hacemos lo mejor posible con nuestro algoritmo para que podamos hacer el try-on 
                            más realista posible, pero la calidad final depende de múltiples factores técnicos 
                            fuera de nuestro control directo.
                          </p>
                        </div>

                        <div className="bg-white/70 rounded-lg p-4 border border-amber-100">
                          <h4 className="font-semibold text-amber-900 mb-2">Otras Limitaciones</h4>
                          <ul className="text-amber-800 text-sm space-y-1">
                            <li>• Algunas prendas pueden no funcionar correctamente con la IA</li>
                            <li>• El procesamiento puede tomar tiempo según la complejidad</li>
                            <li>• Requiere conexión a internet estable</li>
                            <li>• Los resultados pueden variar según el tipo de prenda y material</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 5. Responsabilidades y Limitaciones */}
                <section className="border-l-4 border-red-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                    5. Responsabilidades y Limitaciones
                  </h2>
                  <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-6 border border-red-200">
                    <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      Limitaciones de Responsabilidad
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white/70 rounded-lg p-4 border border-red-100">
                        <h4 className="font-semibold text-red-900 mb-2">Resultados de IA</h4>
                        <p className="text-red-800 text-sm leading-relaxed">
                          Luxify no se hace responsable por discrepancias, errores o resultados 
                          de las pruebas virtuales que no cumplan las expectativas del usuario, 
                          especialmente aquellos causados por limitaciones de los proveedores 
                          externos de inteligencia artificial.
                        </p>
                      </div>
                      
                      <div className="bg-white/70 rounded-lg p-4 border border-red-100">
                        <h4 className="font-semibold text-red-900 mb-2">Otros Aspectos</h4>
                        <ul className="text-red-800 text-sm space-y-1">
                          <li>• Pérdida de datos o contenido subido por el usuario</li>
                          <li>• Interrupciones temporales del servicio</li>
                          <li>• Decisiones comerciales basadas en el uso del servicio</li>
                          <li>• Problemas de conectividad o acceso a internet</li>
                          <li>• Calidad de las imágenes subidas por el usuario</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 6. Modificaciones y Terminación */}
                <section className="border-l-4 border-gray-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <UserX className="h-6 w-6 text-gray-600 mr-3" />
                    6. Modificaciones y Terminación
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Modificaciones</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Nos reservamos el derecho de modificar estos términos en cualquier momento. 
                        Los cambios serán notificados a través del servicio y entrarán en vigor 
                        inmediatamente.
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Terminación</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Cualquiera de las partes puede terminar el servicio en cualquier momento. 
                        En caso de terminación, se notificará con al menos 30 días de anticipación.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 7. Contacto */}
                <section className="border-l-4 border-indigo-500 pl-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Globe className="h-6 w-6 text-indigo-600 mr-3" />
                    7. Contacto y Soporte
                  </h2>
                  <div className="bg-indigo-50 rounded-xl p-6">
                    <p className="text-indigo-700 leading-relaxed">
                      Si tienes preguntas sobre estos términos, necesitas soporte técnico o 
                      quieres reportar algún problema, puedes contactarnos a través de los 
                      canales disponibles en nuestro sitio web.
                    </p>
                  </div>
                </section>
              </div>

              {/* Footer del documento */}
              <div className="border-t border-gray-200 mt-16 pt-8">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Shield className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Compromiso con la Excelencia
                    </h3>
                    <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
                      En Luxify, nos comprometemos a brindar una experiencia de calidad 
                      y transparencia. Estos términos están diseñados para proteger tanto a nuestros 
                      usuarios como a nuestro servicio, asegurando un entorno seguro y confiable 
                      para todos.
                    </p>
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        © {new Date().getFullYear()} Luxify. Todos los derechos reservados.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
